import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Paper, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Select, MenuItem, FormControl, InputLabel, IconButton, Typography,
  Stack, Card, CardContent, Box, Divider, Collapse, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

const API_BASE = 'https://portal.peaksoftcon.com:8081/api';

const FILE_TITLES = [
  'Proje onay yazısı',
  'İş planı',
  'Proje bitirme belgesi',
];

// ── Manuel Çalıştırma Paneli ─────────────────────────────────────────────────
function ManualRunPanel({ companies, onRunComplete }) {
  const [selectedCid, setSelectedCid] = useState('');
  const [runningAll,  setRunningAll]  = useState(false);
  const [runningSingle, setRunningSingle] = useState(false);
  const [logs, setLogs] = useState([]);
  const logRef = useRef(null);

  const addLog = (msg, type = 'info') => {
    const ts = new Date().toLocaleTimeString('tr-TR');
    setLogs((prev) => [...prev.slice(-99), { ts, msg, type }]);
  };

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logs]);

  const runSingle = async () => {
    if (!selectedCid) return;
    const company = companies.find((c) => c.id === selectedCid);
    setRunningSingle(true);
    addLog(`▶ "${company?.name || selectedCid}" için proje çekme başlatıldı…`);
    try {
      await axios.post(`${API_BASE}/bot/companies/${selectedCid}/run-projects`);
      addLog(`  İstek gönderildi, bot çalışıyor…`);
      await new Promise((r) => setTimeout(r, 9000));
      addLog(`  Tamamlandı.`, 'success');
      onRunComplete(selectedCid);
    } catch (e) {
      addLog(`  HATA: ${e?.response?.data?.error || e.message}`, 'error');
    } finally {
      setRunningSingle(false);
    }
  };

  const runAll = async () => {
    if (companies.length === 0) return;
    setRunningAll(true);
    addLog(`▶ Tüm ${companies.length} firma için sırayla çalıştırılıyor…`);
    for (const c of companies) {
      if (!runningAll) break; // iptal edilirse dur
      addLog(`  → "${c.name}" başlatıldı`);
      try {
        await axios.post(`${API_BASE}/bot/companies/${c.id}/run-projects`);
        await new Promise((r) => setTimeout(r, 10000));
        addLog(`    "${c.name}" tamamlandı`, 'success');
        onRunComplete(c.id);
      } catch (e) {
        addLog(`    "${c.name}" HATA: ${e?.response?.data?.error || e.message}`, 'error');
      }
    }
    addLog('▶ Tüm firmalar işlendi.', 'success');
    setRunningAll(false);
  };

  const logColor = { info: 'text.primary', success: 'success.main', error: 'error.main' };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <PlayArrowIcon color="primary" fontSize="small" />
        <Typography variant="subtitle1" fontWeight={600}>Manuel Çalıştırma</Typography>
      </Stack>

      <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
        {/* Tek firma seç + çalıştır */}
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Firma Seç</InputLabel>
          <Select
            label="Firma Seç"
            value={selectedCid}
            onChange={(e) => setSelectedCid(e.target.value)}
            disabled={runningAll}
          >
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          size="small"
          startIcon={runningSingle ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
          disabled={!selectedCid || runningSingle || runningAll}
          onClick={runSingle}
        >
          {runningSingle ? 'Çalışıyor…' : 'Çalıştır'}
        </Button>

        <Divider orientation="vertical" flexItem />

        {/* Tüm firmalar */}
        <Button
          variant="outlined"
          size="small"
          color="warning"
          startIcon={runningAll ? <CircularProgress size={14} color="inherit" /> : <AllInclusiveIcon />}
          disabled={companies.length === 0 || runningAll || runningSingle}
          onClick={runAll}
        >
          {runningAll ? 'Çalışıyor…' : 'Tümünü Çalıştır'}
        </Button>

        {logs.length > 0 && (
          <Button size="small" variant="text" color="inherit" onClick={() => setLogs([])}>
            Temizle
          </Button>
        )}
      </Stack>

      {/* Log çıktısı */}
      {logs.length > 0 && (
        <Box
          ref={logRef}
          sx={{
            mt: 1.5,
            maxHeight: 160,
            overflowY: 'auto',
            bgcolor: 'grey.950',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
            fontFamily: 'monospace',
            fontSize: 12,
          }}
        >
          {logs.map((l, i) => (
            <Box key={i} sx={{ color: logColor[l.type] || 'text.primary' }}>
              <Box component="span" sx={{ color: 'text.disabled', mr: 1 }}>{l.ts}</Box>
              {l.msg}
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

// ── Ana Bileşen ──────────────────────────────────────────────────────────────
function AdminProjectTracking() {
  const [companies, setCompanies]   = useState([]);
  const [projects,  setProjects]    = useState({});
  const [files,     setFiles]       = useState({});
  const [running,   setRunning]     = useState({});
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const [uploadOpen,  setUploadOpen]  = useState(false);
  const [uploadCid,   setUploadCid]   = useState(null);
  const [uploadFile,  setUploadFile]  = useState(null);
  const [uploadTitle, setUploadTitle] = useState(FILE_TITLES[0]);
  const [uploading,   setUploading]   = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/bot/companies`)
      .then((r) => setCompanies(r.data || []))
      .catch((e) => console.error('companies fetch error:', e));
  }, []);

  const fetchProjects = useCallback((cid) => {
    axios.get(`${API_BASE}/bot/companies/${cid}/projects`)
      .then((r) => setProjects((p) => ({ ...p, [cid]: r.data || [] })))
      .catch((e) => console.error('projects fetch error:', e));
  }, []);

  const fetchFiles = useCallback((cid) => {
    axios.get(`${API_BASE}/projectFiles/${cid}`)
      .then((r) => setFiles((f) => ({ ...f, [cid]: r.data || [] })))
      .catch((e) => console.error('files fetch error:', e));
  }, []);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (!projects[id]) fetchProjects(id);
        if (!files[id])    fetchFiles(id);
      }
      return next;
    });
  };

  // Card içindeki "Projeleri Çek" butonu (tek firma, kart içinden)
  const runProjects = async (cid) => {
    setRunning((r) => ({ ...r, [cid]: true }));
    try {
      await axios.post(`${API_BASE}/bot/companies/${cid}/run-projects`);
      setTimeout(() => {
        fetchProjects(cid);
        setRunning((r) => ({ ...r, [cid]: false }));
      }, 9000);
    } catch (e) {
      console.error('run-projects error:', e);
      setRunning((r) => ({ ...r, [cid]: false }));
    }
  };

  // Manuel panel callback: çalışma bitince projeleri yenile
  const handleRunComplete = useCallback((cid) => {
    fetchProjects(cid);
  }, [fetchProjects]);

  const openUpload = (cid) => {
    setUploadCid(cid);
    setUploadFile(null);
    setUploadTitle(FILE_TITLES[0]);
    setUploadOpen(true);
  };

  const submitUpload = async () => {
    if (!uploadFile || !uploadCid) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file',   uploadFile);
    fd.append('baslik', uploadTitle);
    try {
      await axios.post(`${API_BASE}/projectFiles/${uploadCid}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadOpen(false);
      fetchFiles(uploadCid);
    } catch (e) {
      console.error('upload error:', e);
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = (fileId, cid) => {
    if (!window.confirm('Bu dosyayı silmek istediğinize emin misiniz?')) return;
    axios.delete(`${API_BASE}/projectFiles/files/${fileId}`)
      .then(() => fetchFiles(cid))
      .catch((e) => console.error('delete file error:', e));
  };

  const downloadFile = (fileId, fileName) => {
    const a = document.createElement('a');
    a.href = `${API_BASE}/projectFiles/files/${fileId}/download`;
    a.download = fileName || '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Proje Takibi</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Firma projelerini görüntüleyin, belge yükleyin.
      </Typography>

      {/* ── Manuel Çalıştırma ── */}
      <ManualRunPanel companies={companies} onRunComplete={handleRunComplete} />

      {/* ── Firma Kartları ── */}
      <Stack spacing={1.5}>
        {companies.length === 0 && (
          <Paper sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">Bot'a kayıtlı firma bulunamadı.</Typography>
          </Paper>
        )}

        {companies.map((company) => {
          const expanded     = expandedIds.has(company.id);
          const compProjects = projects[company.id] || [];
          const compFiles    = files[company.id]    || [];
          const isRunning    = !!running[company.id];

          return (
            <Card
              key={company.id}
              variant="outlined"
              sx={{
                borderLeft: '4px solid',
                borderLeftColor: compProjects.length > 0 ? 'primary.main' : 'divider',
              }}
            >
              <CardContent
                sx={{ cursor: 'pointer', '&:last-child': { pb: 1.5 } }}
                onClick={() => toggleExpand(company.id)}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <AccountTreeIcon fontSize="small" color="action" />
                    <Typography fontWeight={600}>{company.name}</Typography>
                    {compProjects.length > 0 && (
                      <Chip size="small" label={`${compProjects.length} proje`} color="primary" variant="outlined" />
                    )}
                    {compFiles.length > 0 && (
                      <Chip size="small" label={`${compFiles.length} dosya`} color="secondary" variant="outlined" />
                    )}
                  </Stack>
                  <ExpandMoreIcon
                    sx={{
                      transform: expanded ? 'rotate(180deg)' : 'none',
                      transition: 'transform .2s',
                      flexShrink: 0,
                    }}
                  />
                </Stack>
              </CardContent>

              <Collapse in={expanded}>
                <Divider />
                <CardContent>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={isRunning ? <CircularProgress size={14} /> : <RefreshIcon />}
                      disabled={isRunning}
                      onClick={() => runProjects(company.id)}
                    >
                      {isRunning ? 'Çekiliyor…' : 'Projeleri Çek'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => openUpload(company.id)}
                    >
                      Dosya Yükle
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => { fetchProjects(company.id); fetchFiles(company.id); }}
                    >
                      Yenile
                    </Button>
                  </Stack>

                  {compProjects.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell>Proje Kodu</TableCell>
                            <TableCell>STB Kodu</TableCell>
                            <TableCell>Proje Adı</TableCell>
                            <TableCell>Kategori</TableCell>
                            <TableCell>Onay Durumu</TableCell>
                            <TableCell>Süre</TableCell>
                            <TableCell>T. Başlangıç</TableCell>
                            <TableCell>Tah. Bitiş</TableCell>
                            <TableCell>Ek Süre</TableCell>
                            <TableCell>Bitiş</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {compProjects.map((p) => {
                            const r = p.rawData || {};
                            return (
                            <TableRow key={p.id} hover>
                              <TableCell>{r.ProjeKodu || '-'}</TableCell>
                              <TableCell>{r.BSTBProjeKodu || '-'}</TableCell>
                              <TableCell
                                sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                title={r.Adi}
                              >
                                {r.Adi || '-'}
                              </TableCell>
                              <TableCell>{r.ProjeKategorisi || '-'}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={r.OnayDurumu || '-'}
                                  color={/onay/i.test(r.OnayDurumu || '') ? 'success' : 'default'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>{r.ProjeSuresi ? `${r.ProjeSuresi} ay` : '-'}</TableCell>
                              <TableCell>{r.TeknoparkBaslangicTarihi || '-'}</TableCell>
                              <TableCell>{r.TahminiBitisSuresi || '-'}</TableCell>
                              <TableCell>{r.TalepEdilenTahminiBitisSuresi || '-'}</TableCell>
                              <TableCell>{r.BitisTarihi || '-'}</TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                      Proje verisi yok — "Projeleri Çek" butonuna veya üstteki Manuel Çalıştırma paneline basın.
                    </Alert>
                  )}

                  {compFiles.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Yüklenen Belgeler</Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                              <TableCell>Belge Türü</TableCell>
                              <TableCell>Dosya Adı</TableCell>
                              <TableCell>Tarih</TableCell>
                              <TableCell align="right">İşlem</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {compFiles.map((f) => (
                              <TableRow key={f.id} hover>
                                <TableCell>
                                  <Chip size="small" label={f.baslik} variant="outlined" />
                                </TableCell>
                                <TableCell>{f.file_name}</TableCell>
                                <TableCell>
                                  {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString('tr-TR') : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton size="small" onClick={() => downloadFile(f.id, f.file_name)}>
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => deleteFile(f.id, company.id)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </CardContent>
              </Collapse>
            </Card>
          );
        })}
      </Stack>

      {/* ── Dosya yükleme dialogu ── */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Belge Yükle</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Belge Türü</InputLabel>
            <Select
              label="Belge Türü"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
            >
              {FILE_TITLES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" component="label" fullWidth>
            {uploadFile ? uploadFile.name : 'Dosya Seç…'}
            <input type="file" hidden onChange={(e) => setUploadFile(e.target.files[0])} />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>İptal</Button>
          <Button
            variant="contained"
            onClick={submitUpload}
            disabled={!uploadFile || uploading}
            startIcon={uploading ? <CircularProgress size={14} /> : null}
          >
            {uploading ? 'Yükleniyor…' : 'Yükle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminProjectTracking;
